/**
 * Трёхсторонний мёрдж строк игровых данных.
 *
 * ЗДЕСЬ и только здесь живёт логика «что делать, когда строку правил кто-то ещё».
 * Ни экраны, ни сервисы её не повторяют: они лишь передают снимок `base` в запрос,
 * а дальше работает перехватчик 409 (`conflict-interceptor.ts`).
 *
 * Чистые функции без React и без axios — их можно звать откуда угодно и тестировать.
 */

import {
  FieldConflict,
  GameDataPayload,
  MergeResult,
  MergeSpec,
  MergeValue,
  TranslateFn,
} from '../../types/game-data.types';

/**
 * Канонический вид значения для сравнения.
 *
 * Три вещи, ради которых он нужен:
 * 1. `null`, `undefined`, `''` и `[]` — это одно и то же «пусто». Бэк отдаёт пустое поле как
 *    `null`, форма — как `''`, и без нормализации каждое пустое поле выглядело бы изменённым.
 * 2. Порядок ключей в объектах не важен.
 * 3. Порядок элементов в списках (цены, ограничения по расам) не важен: переставленные местами
 *    строки — не правка, и поднимать из-за них конфликт незачем.
 *
 * Осторожно: `0` и `false` — значащие значения (`markType: 0`), пустыми они НЕ считаются.
 */
const canonical = (value: MergeValue): string => {
  if (value === null || value === undefined || value === '') return '';

  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    return JSON.stringify(value.map(canonical).sort());
  }

  if (typeof value === 'object') {
    const entries = Object.keys(value as Record<string, unknown>)
      .sort()
      .map((key) => [key, canonical((value as Record<string, unknown>)[key])]);
    return JSON.stringify(entries);
  }

  return JSON.stringify(value);
};

/** Сравнение значений полей с учётом всех нормализаций выше. */
export const deepEquals = (a: MergeValue, b: MergeValue): boolean => canonical(a) === canonical(b);

/** Значение считается пустым: `null`, `undefined`, пустая строка, пустой список. */
export const isBlank = (value: MergeValue): boolean => canonical(value) === '';

/** Как показать значение поля человеку, если у поля нет своего `format`. */
export const formatMergeValue = (value: MergeValue, t: TranslateFn): string => {
  if (isBlank(value)) return t('gameData:conflict.empty');
  if (typeof value === 'boolean') return t(value ? 'gameData:conflict.on' : 'gameData:conflict.off');
  if (Array.isArray(value)) return t('gameData:conflict.listCount', { n: value.length });
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Трёхсторонний мёрдж.
 *
 *   base   — снимок строки на момент открытия на редактирование
 *   mine   — то, что человек собрался сохранить
 *   theirs — актуальное состояние с сервера (из `current` ответа 409)
 *
 * По каждому полю:
 *   я не трогал         → берём чужое
 *   они не трогали      → берём моё
 *   значения совпали    → без разницы
 *   иначе               → настоящий конфликт, решает человек
 *
 * Поля вне `spec.fields` (служебные вроде `version`) переносятся из `mine` как есть —
 * версию перехватчик всё равно подставит свежую.
 */
export const threeWayMerge = <TDto>(
  base: GameDataPayload,
  mine: GameDataPayload,
  theirs: GameDataPayload,
  spec: MergeSpec<TDto>,
): MergeResult => {
  const merged: GameDataPayload = { ...mine };
  const conflicts: FieldConflict[] = [];

  spec.fields.forEach((field) => {
    const equals = field.compare ?? deepEquals;
    const baseValue = base[field.key];
    const myValue = mine[field.key];
    const theirValue = theirs[field.key];

    if (equals(myValue, theirValue)) {
      // Пришли к одному и тому же — брать можно любое.
      merged[field.key] = myValue;
      return;
    }

    if (equals(myValue, baseValue)) {
      // Это поле я не трогал — уступаю чужой правке.
      merged[field.key] = theirValue;
      return;
    }

    if (equals(theirValue, baseValue)) {
      // Его не трогали они — остаётся моё.
      merged[field.key] = myValue;
      return;
    }

    // Правили оба и по-разному: сам не решаю.
    conflicts.push({
      key: field.key,
      labelKey: field.labelKey,
      mine: myValue,
      theirs: theirValue,
      format: field.format,
    });
    merged[field.key] = myValue;
  });

  return { merged, conflicts };
};
